
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
      MetaSyncService,
      MetaSyncResponse,
      JsonSchemaPayload,
      MetaFieldDtoo
} from '../../../core/services/meta-sync.service'; // Adjust path as needed
import { HttpClient } from '@angular/common/http';

// 1. Add this declaration at the very top of your .ts file, above @Component
declare var KTToast: any;
@Component({
      selector: 'app-meta-sync-manager',
      standalone: true,
      imports: [CommonModule, FormsModule],
      templateUrl: './meta-sync.component.html',

})
export class MetaSyncManagerComponent implements OnInit {
      entitiesData: MetaSyncResponse[] = [];
      selectedEntity: MetaSyncResponse | null = null;
      selectedField: MetaFieldDtoo | null = null;
      filter?: boolean;
      dialogWidth?: string | number;
      filterColumns?: string | number;
      [key: string]: any;
      // JSON View/Edit State
      jsonEditMode = false;
      editableEntityJsonText = '';
      editableFieldJsonText = '';
      isPublished: boolean = false;
      loading = false;
      
      constructor(private syncService: MetaSyncService, private http: HttpClient) { }

      ngOnInit(): void {
            this.loadAll();
      }

      /**
       * Loads all entities and their fields from the backend[cite: 129].
       */
      loadAll(keepEntityId?: string): void {
            this.loading = true;
            if (!keepEntityId) this.entitiesData = [];
            this.syncService.getAllSyncData().subscribe({
                  next: (data) => {
                        this.entitiesData = data;
                        this.loading = false;

                        if (keepEntityId) {
                              const found = data.find(e => e.fields.some(f => f.entityId === keepEntityId));
                              if (found) this.selectEntity(found);
                        } else if (data.length > 0 && !this.selectedEntity) {
                              this.selectEntity(data[0]);
                        }
                  },
                  error: (err) => {
                        console.error('Load failed', err);
                        this.loading = false;
                  }
            });
      }

      selectEntity(entity: MetaSyncResponse): void {
            this.isPublished = entity.isPublished || false;
            this.selectedEntity = entity;
            this.selectedField = null;
            this.jsonEditMode = false;
            //
            let entityJson =JSON.stringify({
                  name: entity.name, 
                  description: entity.description, 
                  viewmode: entity.viewmode,
                  dataFlow: entity.dataFlow ? JSON.parse(entity.dataFlow) : null,
                  moduleCode: entity.moduleCode, 
                  transType: entity.transType,
                  placement:entity.placement,
                  actions: entity.actions ? JSON.parse(entity.actions) : [],
                  taxConfig: entity.taxConfig ? JSON.parse(entity.taxConfig) : null
                  });
            // Format JSON for the editor
            this.editableEntityJsonText = this.getFormattedJson(entityJson); 
            this.editableFieldJsonText = this.getFormattedJson(entity.uiSchema);
      }

      selectField(field: MetaFieldDtoo): void {
            // Create a deep copy to avoid direct mutation before saving
            this.selectedField = JSON.parse(JSON.stringify(field));
            this.jsonEditMode = false;
      }

      /**
       * MODE 1: Sync a single field row back to the DB and patch the JSON[cite: 131, 150].
       */
      saveFieldUpdate(): void {
            if (!this.selectedField) return;
            this.loading = true;
            const entityId = this.selectedField.entityId;

            this.syncService.syncFieldToUi(this.selectedField).subscribe({
                  next: () => {
                        alert("Field updated and JSON patched successfully!");
                        this.loadAll(entityId);
                        this.selectedField = null;
                  },
                  error: (err) => {
                        alert("Field update failed: " + err.message);
                        this.loading = false;
                  }
            });
      }

      saveFullJson(): void {
            if (!this.selectedEntity) return;

            let sanitizedEntityJson = '';
            let sanitizedFieldJson = '';
            try {
                  
                  const parsedEntity = JSON.parse(this.editableEntityJsonText);
                  sanitizedEntityJson = JSON.stringify(parsedEntity);
                  //
                  const parsedField = JSON.parse(this.editableFieldJsonText);
                  sanitizedFieldJson = JSON.stringify(parsedField);
            } catch (e) {
                  alert("Invalid JSON format.");
                  return;
            }

            this.loading = true;

            // Use the entityId from the selection, or fallback to the first field's entityId
            const entityId = this.selectedEntity.entityId || this.selectedEntity.id || this.selectedEntity.fields[0]?.entityId;

            if (!entityId) {
                  alert("Could not determine Entity ID");
                  this.loading = false;
                  return;
            }

            this.syncService.syncUiToFields(entityId, sanitizedEntityJson, sanitizedFieldJson).subscribe({
                  next: () => {
                        alert("Updated successfully!");
                        this.jsonEditMode = false;
                        this.loadAll(entityId);
                  },
                  error: (err) => {
                        console.error(err);
                        alert("Sync Failed: Check console for 500 error details.");
                        this.loading = false;
                  }
            });
      }

      publishEntity(): void {
            if (!this.selectedEntity) return;
            this.loading = true;

            const entityId = this.selectedEntity.entityId || this.selectedEntity.fields[0]?.entityId;
            // Assuming the backend has an endpoint to set isPublished to true
            this.syncService.publishEntity(entityId!).subscribe ({
                  next: () => {
                        alert("Entity published successfully!");
                        this.isPublished = true;
                        this.loading = false;
                  },
                  error: (err: any) => {
                        console.error(err);
                        alert("Publish Failed: Check console for 500 error details.");
                        this.loading = false;
                  }
            });
      }

      getFormattedJson(json: string): string {
            try {
                  return JSON.stringify(JSON.parse(json), null, 2);
            } catch {
                  return json;
            }
      }

      // For a new Entity Add

      // Modal State
      isModalOpen = false;
      isSubmitting = false;

      // JSON Data Model
      newEntityJson: string = '';

      openModal() {
            this.isModalOpen = true;
            // Default starting template
            this.newEntityJson = JSON.stringify({
                  entity: "",
                  name: "",
                  viewMode: "tab",
                  form: { form: [] }
            }, null, 4);
      }

      closeModal() {
            this.isModalOpen = false;
            this.newEntityJson = '';
      }

      // Utility to format messy or minified JSON
      formatJson() {
            try {
                  const parsed = JSON.parse(this.newEntityJson);
                  this.newEntityJson = JSON.stringify(parsed, null, 4);
            } catch (e) {
                  alert("Invalid JSON format. Please check for missing commas or quotes.");
            }
      }

      submitEntity() {
            if (!this.newEntityJson.trim()) return;

            try {
                  const payload = JSON.parse(this.newEntityJson);
                  this.isSubmitting = true;

                  // Calling the service method
                  this.syncService.createEntity(payload).subscribe({
                        next: (res) => {
                              this.isSubmitting = false;
                              this.closeModal();
                              alert("Entity saved successfully!");
                        },
                        error: (err) => {
                              this.isSubmitting = false;
                              console.error(err);
                              alert("Error saving entity.");
                        }
                  });
            } catch (e) {
                  alert("Invalid JSON: Cannot parse data before sending.");
            }
      }

      deleteEntity(): void {
            if (!this.selectedEntity) return;

            // 1. Capture details for the message
            const entityName = this.selectedEntity.fields[0]?.entityName || 'Unknown Entity';
            const fieldCount = this.selectedEntity.fields.length;
            const entityId = this.selectedEntity.entityId || this.selectedEntity.fields[0]?.entityId;

            // Confirmation updated to warn about the Physical Table
            const confirmMessage = `CRITICAL WARNING: You are about to permanently WIPE:
- Entity: ${entityName}
- Physical Database Table (and all its data)
- ${fieldCount} Associated Fields
- UI Schema Definition

This action cannot be undone. Proceed?`;

            if (confirm(confirmMessage)) {
                  this.loading = true;

                  this.syncService.hardDeleteEntity(entityId).subscribe({
                        next: () => {
                              this.loading = false;

                              // --- SUCCESS TOASTER (GREEN) ---
                              KTToast.show({
                                    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-circle"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`,
                                    message: `SUCCESS: Entity and Physical Table for "${entityName}" have been destroyed.`,
                                    appearance: 'solid',
                                    variant: 'mono', // Changed to success for green color
                                    duration: 5000,
                              });

                              // 2. Clear selection and refresh UI
                              this.selectedEntity = null;
                              this.selectedField = null;
                              this.jsonEditMode = false;
                              this.loadAll();
                        },
                        error: (err) => {
                              this.loading = false;
                              console.error('Delete failed', err);

                              // --- ERROR TOASTER (RED) ---
                              KTToast.show({
                                    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-alert-octagon"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
                                    message: "WIPE FAILED: " + (err.error?.message || "Check server logs for database constraints."),
                                    appearance: 'solid',
                                    variant: 'mono', // Changed to danger for red color
                                    duration: 7000,
                              });
                        }
                  });
            }
      }
      
      deleteSpecificField(field: any): void {
            const entityId = this.selectedEntity?.entityId || field.entityId;
            const fieldName = field.name;

            // --- Validation Toast (Mono) ---
            if (!entityId || !fieldName) {
                  KTToast.show({
                        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
                        message: "Missing Entity ID or Field Name",
                        appearance: 'solid',
                        variant: 'mono',
                        duration: 5000,
                  });
                  return;
            }

            const confirmMsg = `Are you sure you want to delete '${field.label}'?`;

            if (confirm(confirmMsg)) {
                  this.loading = true;

                  this.syncService.deleteField(entityId, fieldName).subscribe({
                        next: (res: any) => {
                              this.loading = false;

                              // --- Success Toast (Mono) ---
                              KTToast.show({
                                    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
                                    message: res.message || "Field deleted successfully",
                                    appearance: 'solid',
                                    variant: 'mono',
                                    duration: 5000,
                              });

                              if (this.selectedEntity) {
                                    this.selectedEntity.fields = this.selectedEntity.fields.filter(f => f.name !== fieldName);
                                    if (this.selectedField?.name === fieldName) {
                                          this.selectedField = null;
                                    }
                              }
                        },
                        error: (err) => {
                              this.loading = false;

                              // --- Error Toast (Mono) ---
                              KTToast.show({
                                    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
                                    message: "Error: " + (err.error?.message || "Failed to delete field"),
                                    appearance: 'solid',
                                    variant: 'mono',
                                    duration: 5000,
                              });
                        }
                  });
            }
      }
}